import * as _ from 'underscore'

import { Enums } from '../common/enums'

import { DataType, ViewColumnType } from '../../helpers/common-helper'
import { DataProvider } from '../../helpers'

import { ActiveViewModel } from '../../modules/api/api.models'
import { ObjectCustomString } from '../util/object-custom-string'

import { OpDataList } from '../util/op-data-list'
import { OpReferenceType } from './op-reference-type'
import { OpViewData } from './op-view-data'
import { OpViewColumn } from './op-view-column'
import { Utils } from '../util/utils'
import { IOpObject, IOpChangeState, IReferenceType } from '../util/interfaces'

export class GetViewColumnOptions {
    public columnName: string = null
    public indexNbr: number = 0
    public viewColumnType: number = ViewColumnType.Unknown
    public keyColumn: OpViewColumn = null

    constructor(public idDataType: number) { }

    public static getViewColumnOptions(
        idDataType: Enums.DataType,
        columnName: string = null,
        indexNbr: number = 0,
        keyColumn: OpViewColumn = null,
        viewColumnType: Enums.ViewColumnType = Enums.ViewColumnType.BaseHelper
    ): GetViewColumnOptions {
        return {
            columnName: columnName,
            idDataType: idDataType,
            indexNbr: indexNbr,
            keyColumn: keyColumn,
            viewColumnType: viewColumnType
        }
    }
}

export class OpView implements IOpObject, IReferenceType, IOpChangeState {
    //#region Properties

    public caption: string
    public columns: OpViewColumn[]
    public idView: number
    public idReferenceType: number
    public name: string

    //#endregion


    //#region NavigationProperties 

    public referenceType: OpReferenceType

    //#endregion


    constructor(opObj: any = null) {
        this.fromJSON(opObj)
    }


    //#region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    //#endregion


    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdView
    }

    public getReferenceKey(): number {
        return this.idView
    }

    public getReferenceValue(): any {
        return this
    }

    //#endregion


    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idView
    }
    public set idObject(idView: any) {
        this.idView = idView
    }

    public dataList: OpDataList<OpViewData> = new OpDataList<OpViewData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.caption = opObj.Caption
            this.idView = opObj.ID_VIEW
            this.idReferenceType = opObj.ID_REFERENCE_TYPE
            this.name = opObj.NAME
            this.opState = opObj.OpState

            if (opObj.DataList)
                this.dataList.push(...opObj.DataList.map(data => new OpViewData(data)))
            else
                this.dataList = new OpDataList<OpViewData>()

            this.referenceType = opObj._ReferenceType ? new OpReferenceType(opObj._ReferenceType) : null

            if (opObj.Columns)
                this.columns = opObj.Columns.map(column => new OpViewColumn(column))
            else
                this.columns = []
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_VIEW: this.idView,
            ID_REFERENCE_TYPE: this.idReferenceType,
            NAME: this.name,
            DataList: this.dataList.ToJSON(),
            Columns: this.columns ? this.columns.map(c => { return c.toJSON() }) : null,
            OpState: this.opState
        }
    }

    public toString() {
        const customFormatString: string = ObjectCustomString.getCustomStringFormat(this)

        if (customFormatString == null) {
            return this.name
        }

        return ObjectCustomString.getObjectCustomString(customFormatString, this)
    }

    //#endregion

    public getViewColumn(options: GetViewColumnOptions): OpViewColumn {
        const columns: OpViewColumn[] = this.getViewColumns(options)

        if (columns.length > 0) {
            return columns[0]
        }

        return null
    }

    public getViewColumns(options: GetViewColumnOptions): OpViewColumn[] {
        const columns = this.columns.filter(c =>
            c.idDataType == options.idDataType
            && c.indexNbr == options.indexNbr
            && (!options.columnName || options.columnName == '' || options.columnName == c.name)
            && (this.idView == 0 || options.viewColumnType == ViewColumnType.Unknown || c.idViewColumnType == options.viewColumnType)
            && (!options.keyColumn || (options.keyColumn && c.idKeyViewColumn == options.keyColumn.idViewColumn))
        )

        return columns
    }

    public getViewColumnByName(name: string): OpViewColumn {
        const columns: OpViewColumn[] = this.columns.filter(c => name == c.name)

        if (columns.length > 0) {
            return columns[0]
        }

        return null
    }

    public getPrimaryKeyColumn() {
        if (this.columns != null) {
            const columns: OpViewColumn[] = this.columns.filter(c => c.primaryKey && c.idDataType != DataType.HELPER_ID_IMR_SERVER)

            if (columns.length > 0) {
                return columns[0]
            }
        }

        return null
    }


    public static restore(restoredOpObj: OpView) {
        if (restoredOpObj != null) {
            const opObj: OpView = new OpView()

            opObj.caption = restoredOpObj.caption
            opObj.columns = restoredOpObj.columns.map(column => OpViewColumn.restore(column))
            opObj.idReferenceType = restoredOpObj.idReferenceType
            opObj.idView = restoredOpObj.idView
            opObj.name = restoredOpObj.name
            opObj.opState = restoredOpObj.opState

            return opObj
        }
        
        return null
    }

    // TODO: remove it when backend will be full disabled
    public static getActiveViewFromActiveViewModel(model: ActiveViewModel) {
        const activeView: OpView = new OpView()

        activeView.caption = model.caption
        activeView.idView = model.id
        activeView.idReferenceType = model.idReferenceType
        activeView.columns = <any>model.columns

        return activeView
    }

    //#region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpView[]): JQueryPromise<OpView[]> {
        if (dataProvider != null) {
            return Utils
                .loadDataListNavigationProperties(dataProvider, list)
                .then(() => {
                    _.forEach(list, (view: OpView) => {
                        OpViewColumn.loadNavigationProperties(dataProvider, view.columns)
                    })

                    return list
                })
        }
    }

    //#endregion
}